import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Home, 
  CheckSquare, 
  Database, 
  ChevronLeft,
  ChevronRight,
  MapPin,
  Map,
  ListChecks,
  Building2,
  ClipboardList,
  FileText,
  PenTool,
  Package
} from 'lucide-react';
import { cn } from '@/utils';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { 
  fetchUserRoles,
  normalizeRoles,
  getUserSystemRoleForOrganization,
  isSystemUserInOrganization,
  isAdminInOrganization,
  isManagerInOrganization
} from '@/utils/roleHelpers';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { usePreview } from '@/contexts/PreviewContext';
import { isSuperAdmin } from '@/utils/adminUtils';
import { Shield } from 'lucide-react';
import Logo from './Logo';
import { useOrganizationType } from '@/hooks/useOrganizationType';

interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
  badge?: string;
  requiresStandard?: boolean; // True if this feature requires Standard+ plan
  requiresComply?: boolean; // True if this feature requires Comply tier
}

const menuItems: MenuItem[] = [
  {
    id: 'home',
    label: 'Home',
    icon: <Home className="w-5 h-5" />,
    path: '/',
  },
  {
    id: 'tasks',
    label: 'Tasks Manager',
    icon: <ClipboardList className="w-5 h-5" />,
    path: '/tasks',
  },
  {
    id: 'compliance-management',
    label: 'Compliances',
    icon: <Database className="w-5 h-5" />,
    path: '/compliance-management',
  },
  {
    id: 'locations',
    label: 'Locations',
    icon: <MapPin className="w-5 h-5" />,
    path: '/locations',
  },
  {
    id: 'floor-map',
    label: 'Floor Map',
    icon: <Map className="w-5 h-5" />,
    path: '/floor-map',
  },
  {
    id: 'agreements',
    label: 'Contracts',
    icon: <PenTool className="w-5 h-5" />,
    path: '/agreements',
    requiresComply: true,
  },
  {
    id: 'checklists',
    label: 'Checklists',
    icon: <ListChecks className="w-5 h-5" />,
    path: '/checklists',
    requiresStandard: true,
  },
  {
    id: 'forms',
    label: 'Forms',
    icon: <FileText className="w-5 h-5" />,
    path: '/forms',
    requiresStandard: true,
  },
  {
    id: 'assets',
    label: 'Assets',
    icon: <Package className="w-5 h-5" />,
    path: '/assets',
    requiresStandard: true,
  },
  {
    id: 'evidence-vault',
    label: 'Evidence Vault',
    icon: <ClipboardList className="w-5 h-5" />,
    path: '/evidence-vault',
    requiresStandard: true, // Evidence Vault is only available for Operate+ tiers
  },
  // {
  //   id: 'compliance-import',
  //   label: 'Import JSON',
  //   icon: <Database className="w-5 h-5" />,
  //   path: '/compliance-import',
  // },
  // {
  //   id: 'firebase-test',
  //   label: 'Firebase Test',
  //   icon: <TestTube className="w-5 h-5" />,
  //   path: '/firebase-test',
  // },
  // {
  //   id: 'database-test',
  //   label: 'Database Test',
  //   icon: <TestTube className="w-5 h-5" />,
  //   path: '/database-test',
  // },
  // {
  //   id: 'settings',
  //   label: 'Settings',
  //   icon: <Settings className="w-5 h-5" />,
  //   path: '/settings',
  //   badge: 'Soon',
  // },
];

interface SidebarProps {
  isCollapsed: boolean;
  onToggle: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ isCollapsed, onToggle }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { currentOrganization } = useOrganization();
  const { hasFeature } = useSubscription();
  const { previewState, isPreviewMode } = usePreview();
  const orgTypeConfig = useOrganizationType();
  const [isSystemUser, setIsSystemUser] = useState<boolean>(false);
  const [previewUserIsSystemUser, setPreviewUserIsSystemUser] = useState<boolean>(false);
  const isAdmin = user?.email ? isSuperAdmin(user.email) : false;

  // Load actual user's role for the current organization
  // This reloads when organization changes to show correct menu items
  useEffect(() => {
    const loadUserRole = async () => {
      if (user?.id && !isPreviewMode && currentOrganization?.id) {
        try {
          const { db } = await import('@/firebase/config');
          const { doc, getDoc } = await import('firebase/firestore');
          const userRef = doc(db, 'users', user.id);
          const userSnap = await getDoc(userRef);
          
          if (userSnap.exists()) {
            const userData = userSnap.data();
            const organizationId = currentOrganization.id;
            
            // Check if user is system "user" role for this organization
            const isSystemUserRole = isSystemUserInOrganization(userData, organizationId);
            setIsSystemUser(isSystemUserRole);
            
            console.log(`[Sidebar] User role for org ${organizationId}: isSystemUser=${isSystemUserRole}`);
          } else {
            // Fallback to legacy fetchUserRoles if user document doesn't exist
            const roles = await fetchUserRoles(user.id);
            setIsSystemUser(roles.isSystemUser);
          }
        } catch (error) {
          console.error('Error loading user role in Sidebar:', error);
          // Fallback to legacy fetchUserRoles on error
          const roles = await fetchUserRoles(user.id);
          setIsSystemUser(roles.isSystemUser);
        }
      } else if (user?.id && !isPreviewMode && !currentOrganization?.id) {
        // No organization selected - fall back to legacy role check
        const roles = await fetchUserRoles(user.id);
        setIsSystemUser(roles.isSystemUser);
      }
    };
    loadUserRole();
  }, [user?.id, currentOrganization?.id, currentOrganization?.name, isPreviewMode]);

  // Load preview user's role when in preview mode
  useEffect(() => {
    const loadPreviewUserRole = async () => {
      if (isPreviewMode && previewState?.previewType === 'user' && previewState.previewUserId) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const previewUserRef = doc(db, 'users', previewState.previewUserId);
          const previewUserSnap = await getDoc(previewUserRef);
          
          if (previewUserSnap.exists()) {
            const previewUserData = previewUserSnap.data();
            const organizationId = currentOrganization?.id;
            
            let isSystemUserRole = false;
            
            // Use organization-specific roles if organization is available
            if (organizationId) {
              isSystemUserRole = isSystemUserInOrganization(previewUserData, organizationId);
            } else {
              // Fallback to global roles if no organization
              const previewUserRoles = normalizeRoles(previewUserData?.roles || previewUserData?.role);
              const SYSTEM_ROLES_LIST = ['user', 'manager', 'admin'];
              const systemRoles = previewUserRoles.filter((r: string) => SYSTEM_ROLES_LIST.includes(r.toLowerCase()));
              isSystemUserRole = systemRoles.length === 1 && systemRoles[0].toLowerCase() === 'user';
            }
            
            setPreviewUserIsSystemUser(isSystemUserRole);
            setIsSystemUser(isSystemUserRole); // Use preview user's status for menu filtering
          }
        } catch (error) {
          console.error('Error loading preview user role:', error);
        }
      } else if (isPreviewMode && previewState?.previewType === 'role') {
        // When previewing by role, check if the role is "user"
        const isSystemUserRole = previewState.role.toLowerCase() === 'user';
        setPreviewUserIsSystemUser(isSystemUserRole);
        setIsSystemUser(isSystemUserRole);
      } else {
        // Not in preview mode, reset
        setPreviewUserIsSystemUser(false);
      }
    };
    loadPreviewUserRole();
  }, [isPreviewMode, previewState?.previewType, previewState?.previewUserId, previewState?.role]);

  // Filter menu items based on user role and organization type
  const filteredMenuItems = menuItems.filter((item) => {
    // Filter by organization type configuration
    const menuKey = item.id as keyof typeof orgTypeConfig.menuItems;
    if (orgTypeConfig.menuItems[menuKey] === false) {
      return false;
    }
    
    // Hide "Locations" and "Compliances" for system role "user"
    if (isSystemUser) {
      if (item.id === 'locations' || item.id === 'floor-map' || item.id === 'compliance-management') {
        return false;
      }
    }
    // Show all menu items regardless of subscription tier
    // Access control is handled at the page level with upgrade messages
    return true;
  });

  return (
    <div className={cn(
      "bg-white border-r border-gray-200 transition-all duration-300 flex flex-col",
      isCollapsed ? "w-16" : "w-64"
    )}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between relative">
          {!isCollapsed ? (
            /* Complin Logo - Centered */
            <div className="absolute left-1/2 transform -translate-x-1/2">
              <Logo size="md" showText={true} />
            </div>
          ) : (
            <div className="mx-auto">
              <Logo size="sm" iconOnly={true} />
            </div>
          )}
          <button
            onClick={onToggle}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 ml-auto"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4 text-gray-600" />
            ) : (
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            )}
          </button>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 overflow-y-auto">
        <ul className="space-y-2">
          {/* All Menu Items */}
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <li key={item.id}>
                <Link
                  to={item.path}
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group",
                    isActive
                      ? "bg-primary-50 text-primary-700 border-r-2 border-primary-600"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  title={isCollapsed ? item.label : undefined}
                >
                  <span className={cn(
                    "flex-shrink-0",
                    isActive ? "text-primary-600" : "text-gray-500 group-hover:text-gray-700"
                  )}>
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 font-medium">{item.label}</span>
                      {item.badge && (
                        <span className="px-2 py-1 text-xs bg-gray-100 text-gray-600 rounded-full">
                          {item.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>
              </li>
            );
          })}

          {/* Admin Support Section - Always show to super admins, even in preview mode */}
          {isAdmin && (
            <>
              <li className="pt-2 pb-1">
                {!isCollapsed && (
                  <div className="flex items-center gap-2 px-3 py-1.5">
                    <div className="flex-1 h-px bg-gray-200"></div>
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Admin
                    </span>
                    <div className="flex-1 h-px bg-gray-200"></div>
                  </div>
                )}
              </li>
              <li>
                <Link
                  to="/admin-support"
                  className={cn(
                    "flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors group",
                    location.pathname === '/admin-support'
                      ? "bg-primary-50 text-primary-700 border-r-2 border-primary-600"
                      : "text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                  )}
                  title={isCollapsed ? 'Admin Support' : undefined}
                >
                  <span className={cn(
                    "flex-shrink-0",
                    location.pathname === '/admin-support' ? "text-primary-600" : "text-gray-500 group-hover:text-gray-700"
                  )}>
                    <Shield className="w-5 h-5" />
                  </span>
                  {!isCollapsed && (
                    <span className="flex-1 font-medium">Admin Support</span>
                  )}
                </Link>
              </li>
            </>
          )}
        </ul>
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200">
        {!isCollapsed && (
          <div className="text-xs text-gray-500 text-center">
            <p>Compliance Management</p>
            <p>v1.0.0</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Sidebar;

